import { Box, Card, NavList } from '@deckee/deck-hand';
import React, { FC, MouseEvent } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { captureEvent } from '../../../../services/event-capture';
import { hasFeatureEnabled } from '../../../../services/feature';
import { openWithInAppBrowser } from '../../../../services/in-app-browser';
import useIsInBounds from '../../../hooks/use-is-in-bounds';
import BasicDrawerLayout from '../../../layouts/BasicDrawerLayout/BasicDrawerLayout';
import { LayoutBody } from '../../../layouts/components/LayoutBody/LayoutBody';
import UserHeaderBar from '../../../shared/UserHeaderBar/UserHeaderBar';
import {
  identityCard,
  messages,
  shop,
  spanner,
} from '../../../shared/vector-images';
import reminderImage from '../assets/reminder_button_image.jpg';
import EmptyInfoPanel from '../../../shared/EmptyInfoPanel/EmptyInfoPanel';
import PartnersCard from '../components/PartnersCard/PartnersCard';
import SafetyGearContainer from '../components/SafetyGearContainer/SafetyGearContainer';
import SafetyGearListItem from '../components/SafetyGearListItem/SafetyGearListItem';

const StyledReminderButtonImage = styled.img`
  max-width: 80px;

  @media screen and (min-width: 350px) {
    max-width: 120px;
  }
  @media screen and (min-width: 650px) {
    max-width: 150px;
  }
  @media screen and (min-width: 800px) {
    max-width: 120px;
  }
`;

const PrepareHomePage: FC = () => {
  const history = useHistory();
  const canPrepareAus = useIsInBounds('AUSTRALIA');
  const canPrepareUSA = useIsInBounds('UNITEDSTATES');
  const canPrepareHawaii = useIsInBounds('HAWAII');
  const canPrepareAlaska = useIsInBounds('ALASKA');

  const safetyRecommendationsEnabled = hasFeatureEnabled(
    'safety-recommendations',
  );

  const canPrepare = safetyRecommendationsEnabled
    ? canPrepareAus || canPrepareUSA || canPrepareHawaii || canPrepareAlaska
    : false;

  const handleClickMenuItem = async (
    name: string,
    e?: MouseEvent<HTMLElement>,
    url?: string,
  ) => {
    captureEvent({
      type: `prepare: click menu item (${name})`,
      payload: {},
      sendToAmplitude: true,
    });

    // Trying to detect if it's an internal URL. if it is, just return true so the default navigation can happen
    if (url?.charAt(0) === '/' && url?.charAt(1) !== '/') {
      return true;
    }

    if (url) {
      try {
        e?.preventDefault();
        e?.stopPropagation();
        await openWithInAppBrowser({ url });
      } catch (err) {
        // Something has gone wrong, just navigate as usual
        return true;
      }
    }

    // Prevent the default navigation
    return false;
  };
  return (
    <BasicDrawerLayout renderHeaderBar={<UserHeaderBar heading="Prepare" />}>
      <LayoutBody>
        {canPrepare && <SafetyGearContainer />}
        <PartnersCard />
        <Card borderStyle="solid" borderWidth="0.5px" borderColor="#dfdfdf">
          <Card.Heading>Be prepared</Card.Heading>
          <Card.Body>
            <NavList edge>
              {hasFeatureEnabled('deckee-shop') && (
                <SafetyGearListItem
                  onClick={(e, url) =>
                    handleClickMenuItem('Shop deals', e, url)
                  }
                  href="//shop.deckee.com"
                  heading="Shop deals on gear"
                  description="Get safety equipment and more"
                  icon={shop()}
                />
              )}

              {hasFeatureEnabled('courses') && (
                <SafetyGearListItem
                  onClick={(e, url) =>
                    handleClickMenuItem('Book courses', e, url)
                  }
                  href="/maps/courses"
                  heading="Book a course"
                  description="Improve your confidence"
                  icon={identityCard()}
                />
              )}
              <SafetyGearListItem
                onClick={(e, url) =>
                  handleClickMenuItem('Find services', e, url)
                }
                href="/maps/marketplace/categories"
                heading="Discover local services"
                description="Find a trusted provider near you"
                icon={spanner()}
              />
              <SafetyGearListItem
                onClick={(e, url) =>
                  handleClickMenuItem('Community chat', e, url)
                }
                href="//community.deckee.com/"
                heading="Chat with the community"
                description="Learn from others on the water"
                icon={messages()}
              />
            </NavList>
          </Card.Body>
        </Card>
        <Box
          mb="2"
          bg="white"
          borderRadius="12px"
          border="0.5px solid #dfdfdf"
          display="flex"
          flex="1 1 auto"
          justifyContent="flex-start"
          alignItems="center"
          p={3}
          as="a"
          href="/maps/reminders"
          color="black"
          onClick={async (e: MouseEvent<HTMLElement>) => {
            e.preventDefault();
            e.stopPropagation();
            const shouldNavigate = await handleClickMenuItem(
              'Reminders',
              e,
              '/maps/reminders',
            );
            if (shouldNavigate) {
              history.push('/maps/reminders');
            }
          }}
        >
          <Box>
            <StyledReminderButtonImage
              src={reminderImage}
              alt="Reminders - Get notified when your equipment is expiring"
            />
          </Box>
          <Box pl={4} py={3} hyphens="manual">
            <Box mb={2} fontSize={2} fontWeight="bold">
              Add your reminders
            </Box>
            <Box color="darkergrey">
              Get notified when your equipment is expiring
            </Box>
          </Box>
        </Box>
        {!canPrepare && (
          <Card borderStyle="solid" borderWidth="0.5px" borderColor="#dfdfdf">
            <Card.Heading>Mandatory equipment</Card.Heading>
            <Card.Body>
              <EmptyInfoPanel>
                <strong>Coming soon:</strong> Check the mandatory equipment for
                your watercraft on any waterway.
              </EmptyInfoPanel>
            </Card.Body>
          </Card>
        )}
      </LayoutBody>
    </BasicDrawerLayout>
  );
};

export default PrepareHomePage;
